import { type JSX, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router';

import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { renderAiDocument } from 'Components/ArtificialIntelligence/AiDocumentRenderer';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { RBACContext, userHasAuthorizedRole } from 'Components/Context/RBACContext';
import { FieldLabel } from 'Components/FieldLabel/FieldLabel';
import { UploadedFileAndState } from 'Components/Files/UploadedFileAndState';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Breadcrumb, BreadcrumbLink, BreadcrumbText } from 'Components/Nav/Breadcrumb/Breadcrumb';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_DELETE_REMOVE, ICON_DOWNLOAD } from 'Config/Icons';
import { AI_DOC_ANALYZER_LISTING } from 'Config/PathTemplates';
import { iso8601ToUsDateLong, iso8601ToUsDateShort } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { downloadDocument } from 'Helpers/FileUtils';
import { toTitleCase } from 'Helpers/StringUtils';
import { TPRM_ROLES, UserNameFormat, getUserNameFromSubject } from 'Helpers/UserUtils';
import { AiDocAnalysis } from 'Models/ArtificialIntelligence';

import styles from './AiDocAnalyzer.module.css';

interface UrlParams {
    id: string;
}

export interface AiDocAnalyzerDetailsProps {
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    documentApi: DocumentApi;
    tprmApi: ClientTPRMApi;
}

export const AiDocAnalyzerDetails = (props: AiDocAnalyzerDetailsProps): JSX.Element => {
    const cachedData = useCachedData();
    const rbacContext = useContext(RBACContext);
    const { id: documentAnalysisId } = useParams<keyof UrlParams>() as UrlParams;

    const [documentAnalysis, setDocumentAnalysis] = useState<AiDocAnalysis>();
    const [isDeleted, setIsDeleted] = useState<boolean>(false);
    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState<boolean>(false);
    const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
    const [thirdPartyName, setThirdPartyName] = useState<string>();
    const [thirdPartyFolderName, setThirdPartyFolderName] = useState<string>();

    /** Get the details for the Document Analysis to be rendered on the page. */
    useEffect(() => {
        const aiDocAnalyzerGet = async (): Promise<void> => {
            try {
                const response = await props.artificialIntelligenceApi.aiDocAnalyzerGet(documentAnalysisId);
                setDocumentAnalysis(response.data);
            } catch (error) {
                setLoadingErrorOccurred(error);
            }
        };

        aiDocAnalyzerGet();
    }, [props.artificialIntelligenceApi, documentAnalysisId]);

    /** Get the details for the Third Party associated with the Document Analysis. */
    useEffect(() => {
        const getThirdPartyDetails = async (): Promise<void> => {
            if (documentAnalysis?.third_party_id) {
                const response = await props.tprmApi.getThirdPartyDetails(documentAnalysis.third_party_id);
                setThirdPartyName(response.data.name);
            }
        };

        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, TPRM_ROLES)) {
            getThirdPartyDetails();
        }
    }, [props.tprmApi, documentAnalysis?.third_party_id, rbacContext]);

    /** Get the details for the Third-Party Folder associated with the Document Analysis. */
    useEffect(() => {
        const getThirdPartyFolderDetails = async (): Promise<void> => {
            if (documentAnalysis?.third_party_id && documentAnalysis?.third_party_folder_id) {
                const response = await props.tprmApi.getAllCurrentFoldersForThirdParty(documentAnalysis.third_party_id);
                const folder = response.data.find((f) => f.id === documentAnalysis.third_party_folder_id);
                if (folder) {
                    setThirdPartyFolderName(folder.name);
                }
            }
        };

        if (rbacContext && userHasAuthorizedRole(rbacContext.roles, TPRM_ROLES)) {
            getThirdPartyFolderDetails();
        }
    }, [props.tprmApi, documentAnalysis?.third_party_id, documentAnalysis?.third_party_folder_id, rbacContext]);

    /** Delete the Document Analysis. */
    const deleteHandler = async () => {
        await props.artificialIntelligenceApi.aiDocAnalyzerDelete(documentAnalysis!);
        setIsDeleted(true);
        return 'Document analysis deleted.';
    };

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    } else if (!documentAnalysis) {
        return <Placeholder />;
    }

    return (
        <>
            {showDeleteConfirmationModal && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM} headerText="Delete Document Analysis" areYouSureText={`Are you sure you want to delete this document analysis?`} performOperation={deleteHandler} hideModal={() => setShowDeleteConfirmationModal(false)}>
                    <Text>This will permanently delete the document analysis.</Text>
                    {documentAnalysis.third_party_folder_id && <Text>The third-party folder and its contents will not be affected.</Text>}
                    {documentAnalysis.create_exceptions && <Text>Any exceptions that were created will not be affected.</Text>}
                </ConfirmationModal>
            )}
            <PageLayoutDefault
                headerBreadcrumb={
                    <Breadcrumb textColor="blue">
                        <BreadcrumbLink link={AI_DOC_ANALYZER_LISTING}>Document Analyzer</BreadcrumbLink>
                        <BreadcrumbText>Document Analysis</BreadcrumbText>
                    </Breadcrumb>
                }
                headerTitle="Document Analysis"
                headerButtons={
                    <>
                        <Button variant="danger" disabled={isDeleted} onClick={() => setShowDeleteConfirmationModal(true)} fontAwesomeImage={ICON_DELETE_REMOVE}>
                            Delete
                        </Button>
                        {documentAnalysis.summary_file && (
                            <Button variant="secondary" onClick={() => downloadDocument(props.documentApi, documentAnalysis.summary_file!)} fontAwesomeImage={ICON_DOWNLOAD}>
                                Download
                            </Button>
                        )}
                    </>
                }
                body={[
                    {
                        content: (
                            <div className={styles.metadataSection}>
                                <div>
                                    <FieldLabel fieldName="Name" />
                                    <UploadedFileAndState documentApi={props.documentApi} file={documentAnalysis.input_file} onDownloadError={console.log} />
                                </div>
                                <div>
                                    <FieldLabel fieldName="Type" />
                                    <Text>{documentAnalysis.document_type}</Text>
                                </div>
                                <div>
                                    <FieldLabel fieldName="Created" />
                                    <Text>{`${iso8601ToUsDateShort(documentAnalysis.created_timestamp)} by ${getUserNameFromSubject(documentAnalysis.created_by, cachedData.users, UserNameFormat.FIRST_SPACE_LAST)}`}</Text>
                                </div>
                                {thirdPartyName && (
                                    <>
                                        <div>
                                            <FieldLabel fieldName="Third Party" />
                                            <Text>{thirdPartyName}</Text>
                                        </div>
                                        <div>
                                            <FieldLabel fieldName="Create Exceptions" />
                                            <Text>{toTitleCase(JSON.stringify(documentAnalysis.create_exceptions))}</Text>
                                        </div>
                                    </>
                                )}
                                {thirdPartyFolderName && (
                                    <div>
                                        <FieldLabel fieldName="Third-Party Folder" />
                                        <Text>{thirdPartyFolderName}</Text>
                                    </div>
                                )}
                                {documentAnalysis.expiration_date && (
                                    <div>
                                        <FieldLabel fieldName="Expiration Date" />
                                        <Text>{iso8601ToUsDateLong(documentAnalysis.expiration_date)}</Text>
                                    </div>
                                )}
                            </div>
                        ),
                    },
                    ...(documentAnalysis.summary
                        ? renderAiDocument(documentAnalysis.summary)
                        : [
                              {
                                  content: <Text>The document is being analyzed. Refresh the page in a few minutes to see the results.</Text>,
                              },
                          ]),
                ]}
            />
        </>
    );
};
